import { DummyInput, Input, Select } from '@/components/Form'
import { useOfferCostBudget } from '@/features/offerCostBudgets/useOfferCostBudget'
import { formatOffer } from '@/features/offers/helpers/formatOffer'
import useOffer from '@/features/offers/hooks/useOffer'
import { useConfirmationModalManagement } from '@/stores/ui/confirmationModalSlice'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'
import { numberWithCommas } from '@/utils/stringHelper'
import { Button, Flex, FormLabel, SimpleGrid } from '@chakra-ui/react'
import { CostBudgetType, CostBudgetTypeLabel, TenderDocumentStatus } from '@eapproval/offer-shared'
import { useFormContext, useFormState, useWatch } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import { useCostBudgetAccess } from './CostBudgetAccessProvider'

const CostBudgetGeneralInfo = ({ costBudgetIndex = -1 }) => {
  const subSystems = useWatch({ name: 'subSystems' })
  const sourcingThreshold = useWatch({ name: `sourcing.sourcingThreshold` })
  const exceedThreshold = useWatch({ name: `sourcing.costBudgets.${costBudgetIndex}.detail.exceedThreshold` }) ?? false
  const tenderDocuments = useWatch({ name: `sourcing.costBudgets.${costBudgetIndex}.detail.tenderDocuments` }) ?? []

  const costBudgetType = useWatch({ name: `sourcing.costBudgets.${costBudgetIndex}.detail.type` })
  const requireProcurementOfficer =
    useWatch({ name: `sourcing.costBudgets.${costBudgetIndex}.detail.requireProcurementOfficer` }) ?? false

  const { getValues, setValue } = useFormContext()

  const { isDirty, dirtyFields } = useFormState()
  const { open: openConfirmationModal } = useConfirmationModalManagement()
  const { useNoticeProcurementOfficer } = useOfferCostBudget()
  const { useUpdateOffer } = useOffer()

  const dispatch = useDispatch()
  const updateOffer = useUpdateOffer()
  const noticeProcurementOfficer = useNoticeProcurementOfficer()
  const { canEditCostBudget, canEditCostBudgetType, canEditCostBudgetSubSystem } = useCostBudgetAccess()

  const subSystemOptions = Array.isArray(subSystems)
    ? subSystems.map((subSystem) => {
        return {
          label: subSystem?.projectName || '',
          value: subSystem?.projectName || '',
        }
      })
    : []

  const handleNoticeProcurementOfficer = async () => {
    const currentOffer = getValues()
    const costBudgetId = getValues(`sourcing.costBudgets.${costBudgetIndex}.detail._id`)
    if (!costBudgetId) {
      toast.error(`Cost Budget ${costBudgetIndex + 1} Please save first to proceed`)
      return
    }
    if (isDirty || Object.keys(dirtyFields).length > 0) {
      //Form has been
      const { isConfirmed } = await openConfirmationModal({
        title: 'Confirm changes to the offer',
        message: "You've modified the offer, are you sure to save the changes to proceed ? ",
      })

      if (!isConfirmed) {
        //Stop current action, and don't open approval modal
        return
      }

      try {
        dispatch(showLoadingOverlay())

        const formattedOffer = formatOffer(currentOffer)

        await updateOffer.mutateAsync({
          id: currentOffer._id,
          data: formattedOffer,
        })
      } catch (error) {
        console.error(error)
        return
      } finally {
        dispatch(hideLoadingOverlay())
      }
    }

    try {
      const costBudgetId = getValues(`sourcing.costBudgets.${costBudgetIndex}.detail._id`)
      const offerId = getValues('_id')

      await noticeProcurementOfficer.mutateAsync({
        costBudgetId: costBudgetId,
        offerId: offerId,
      })
    } catch (err) {
      toast.error(`Cost Budget ${costBudgetIndex + 1} Failed to notice procurement officer: ${err.message ?? ''}`)
    }
  }

  const handleCostBudgetTypeChange = (type) => {
    //Get Latest TenderDocument
    const tenderDocumentIndex = tenderDocuments?.length ? tenderDocuments.length - 1 : 0

    setValue(
      `sourcing.costBudgets.${costBudgetIndex}.detail.tenderDocuments.${tenderDocumentIndex}.quotationTemplate.details`,
      []
    )
  }
  return (
    <SimpleGrid columns={[3, null, 3]} spacingX="40px" spacingY="10px">
      <Select
        name={`sourcing.costBudgets.${costBudgetIndex}.detail.subSystem`}
        label={'Subsystem'}
        options={subSystemOptions}
        isRow
        disabled={!canEditCostBudgetSubSystem}
      />
      <Input
        name={`sourcing.costBudgets.${costBudgetIndex}.detail.purpose`}
        label={'Scope of supply'}
        isRow
        disabled={!canEditCostBudget}
      />

      {costBudgetType === CostBudgetType.SUPPLY_SITE_WORK_RFQ ? (
        <DummyInput label={'Type'} value={CostBudgetTypeLabel[CostBudgetType.SUPPLY_SITE_WORK_RFQ]} isRow />
      ) : (
        <Select
          name={`sourcing.costBudgets.${costBudgetIndex}.detail.type`}
          label={'Type'}
          options={Object.values(CostBudgetType)
            .filter((type) => type !== CostBudgetType.SUPPLY_SITE_WORK_RFQ)
            .map((type) => {
              return {
                label: CostBudgetTypeLabel[type],
                value: type,
              }
            })}
          isRow
          disabled={!canEditCostBudgetType}
          extraOnChange={handleCostBudgetTypeChange}
        />
      )}

      <DummyInput
        label={`Cost Budget (> $${numberWithCommas(sourcingThreshold)})`}
        value={exceedThreshold ? 'Yes' : 'No'}
        isRow
      />

      <Input
        name={`sourcing.costBudgets.${costBudgetIndex}.detail.RFQCode`}
        label={'RFQ Reference'}
        isRow
        disabled={true}
        isRequired={false}
      />
      {requireProcurementOfficer ? (
        <DummyInput label={`Require Procurement Officer (>$100K)`} value={'Yes'} isRow />
      ) : (
        <Flex alignItems={'center'}>
          <FormLabel w="30%">Require Procurement Officer (&gt;$100K)</FormLabel>

          {canEditCostBudget && (
            <Button colorScheme="teal" onClick={handleNoticeProcurementOfficer}>
              Notice Procurement Officer
            </Button>
          )}
        </Flex>
      )}
    </SimpleGrid>
  )
}

export default CostBudgetGeneralInfo
